import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../App';
import io from 'socket.io-client';
import { socketOrigin } from '../services/socketOrigin';
import toast from 'react-hot-toast';
import { Banknote, Hourglass, Search, Send, Star, Trash2, UserPlus, Zap } from 'lucide-react';
import { Empty } from '../ui/kit';
import PageHeader from '../components/PageHeader';
import { avatarSrc } from '../lib/avatarSrc';
import { CONTACT_HINT, inviteContactKind } from '../lib/inviteContact';
// ZEIT-EINHEIT-PORTAL-001: Einladungsfristen sind UTC — roh gelesen wirkte eine Einladung
// je nach Uhrzeit einen Tag kuerzer, als sie ist.
import { nurDatum } from '../lib/zeit';

// PARTNER-FAHRERLISTE-KOMPAKT-001: Dokumente, Fahrzeug und Standort bleiben im Profil. Die spätere
// Owner-Erweiterung PARTNER-FAHRER-KATEGORIEN-ANZEIGE-001 nimmt die wirksamen Kategorien bewusst
// wieder direkt in die Karte auf, einschließlich verbindlichem Partner-Schalter.
const statusBadge = {
  pending:  { label: 'Wird geprüft', color: '#4b5563', bg: '#f3f4f6' },
  rejected: { label: 'Abgelehnt',   color: '#dc2626', bg: '#fef2f2' },
};

const visibleEntityRejectionReason = (status, reason) => status === 'rejected'
  ? String(reason || '').trim()
  : '';

const CATEGORY_LABELS = {
  standard: 'VOOGO Basis',
  comfort: 'VOOGO Komfort',
  premium: 'VOOGO Business',
  xl: 'VOOGO Family',
  max: 'VOOGO Van',
  taxi: 'VOOGO Taxi',
};

const canonicalCategories = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)
      || !Array.isArray(value.allowed) || !Array.isArray(value.active)
      || !value.allowed.every(item => typeof item === 'string')
      || !value.active.every(item => value.allowed.includes(item))
      || !value.partner_controlled || typeof value.partner_controlled !== 'object'
      || Array.isArray(value.partner_controlled)) return false;
  return Object.entries(value.partner_controlled)
    .every(([category, enabled]) => value.allowed.includes(category) && typeof enabled === 'boolean');
};

// Echtes Profilfoto (vom API-Host, s. lib/avatarSrc); ohne Foto oder bei Ladefehler der Anfangsbuchstabe.
function DriverAvatar({ driver }) {
  const src = avatarSrc(driver.avatar);
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  const online = !!driver.is_online;
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <div data-testid={`driver-avatar-${driver.id}`}
        style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', background: online ? '#000' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: online ? '#fff' : '#6b7280' }}>
        {src && !broken
          ? <img src={src} alt="" onError={() => setBroken(true)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          : driver.name?.charAt(0)}
      </div>
      <div style={{ position: 'absolute', bottom: 0, right: 0, width: 13, height: 13, borderRadius: '50%', background: online ? '#9C27B0' : '#d1d5db', border: '2px solid #fff', boxShadow: online ? '0 0 6px #9C27B0' : 'none' }} />
    </div>
  );
}

export default function Drivers() {
  const { partner } = useAuth();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [view, setView] = useState('fleet'); // 'fleet' | 'invite'
  const [fleetSearch, setFleetSearch] = useState('');   // Flotte durchsuchen (Name/E-Mail/Telefon)
  const [categorySaving, setCategorySaving] = useState({});
  const categoryLocksRef = useRef(new Set());

  // PARTNER-FAHRER-EINLADEN-KONTAKT-001: keine Trefferliste fremder Fahrer mehr — der Partner gibt
  // E-Mail oder Telefonnummer ein, der Server sagt „eingeladen" oder was nicht passt.
  const [inviteContact, setInviteContact] = useState('');
  const [contactLoading, setContactLoading] = useState(false);
  const [contactFeedback, setContactFeedback] = useState(null); // { kind: 'ok' | 'error', text, hint? }

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [lastInvite, setLastInvite] = useState('');

  const socketRef = useRef(null);

  const load = useCallback(() => {
    api.get('/partners/drivers').then(r => setDrivers(r.data.drivers || []));
    api.get('/partners/invitations').then(r => setInvitations(r.data.invitations || []));
  }, []);

  useEffect(() => {
    load();
    // Online-/Statuswechsel live nachladen
    const socket = io(socketOrigin(), { auth: { token: localStorage.getItem('partner_token') }, extraHeaders: { 'ngrok-skip-browser-warning': 'true' } });
    socketRef.current = socket;
    socket.on('driver_status_changed', () => load());
    return () => socket.disconnect();
  }, [load]);

  // Flotte alle 15 Sekunden nachladen (online, Fahrten, Bewertung)
  useEffect(() => {
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  const sendContactInvite = async (e) => {
    e.preventDefault();
    const contact = inviteContact.trim();
    if (!inviteContactKind(contact)) {
      setContactFeedback({ kind: 'error', text: CONTACT_HINT });
      return;
    }
    setContactLoading(true);
    setContactFeedback(null);
    try {
      await api.post('/partners/invite-driver', { contact });
      setContactFeedback({ kind: 'ok', text: `Einladung an ${contact} gesendet. Der Fahrer bestätigt sie in seiner App.` });
      toast.success('Einladung gesendet');
      setInviteContact('');
      load();
    } catch (err) {
      const data = err.response?.data || {};
      setContactFeedback({
        kind: 'error',
        text: data.message || data.error || 'Einladung fehlgeschlagen. Bitte später erneut versuchen.',
        hint: data.error === 'driver_not_found' && data.contact_type === 'email'
          ? 'Noch nicht registriert? Dann lade ihn unter „Neuen Fahrer einladen“ per E-Mail ein.'
          : null,
      });
    } finally { setContactLoading(false); }
  };

  const sendEmailInvite = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    try {
      // FAHRER-EINLADUNG-TOT-001: Frueher kam hier nur ein Link zurueck, den der Betrieb selbst
      // verschicken musste — und der ins Leere fuehrte. Jetzt verschickt der Server eine echte
      // E-Mail und meldet ehrlich, ob sie rausging.
      await api.post('/partners/invite', { email: inviteEmail });
      setLastInvite(inviteEmail);
      toast.success(`Einladung an ${inviteEmail} gesendet.`);
      setInviteEmail(''); load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Fehler');
    }
    finally { setInviteLoading(false); }
  };

  const toggleSetting = async (driverId, field, currentVal) => {
    try {
      const res = await api.patch(`/partners/drivers/${driverId}`, { [field]: !currentVal });
      setDrivers(prev => prev.map(d => d.id === driverId ? { ...d, ...res.data.driver } : d));
      toast.success(field === 'cash_enabled'
        ? (!currentVal ? 'Bargeld aktiviert' : 'Bargeld deaktiviert')
        : (!currentVal ? 'Auto-Annahme aktiviert' : 'Auto-Annahme deaktiviert')
      );
    } catch (err) { toast.error(err.response?.data?.error || 'Fehler'); }
  };

  const toggleCategory = async (driver, category, currentVal) => {
    const driverId = driver.id;
    const desired = !currentVal;
    // Ein Fahrer = eine Schreibspur. Der Ref sperrt synchron, noch bevor React neu rendert.
    if (categoryLocksRef.current.has(driverId)) return;
    categoryLocksRef.current.add(driverId);
    setCategorySaving(current => ({ ...current, [driverId]: true }));
    try {
      const res = await api.patch(`/partners/drivers/${driverId}`, {
        category_control: { category, enabled: desired },
      });
      let canonical = res?.data?.driver;
      const confirmed = item => canonicalCategories(item?.categories)
        && item.categories.active.includes(category) === desired
        && item.categories.partner_controlled[category] === desired;
      if (!confirmed(canonical)) {
        // Unklare oder widersprüchliche Antwort niemals optimistisch anzeigen: kanonisch neu lesen.
        const fresh = await api.get('/partners/drivers');
        canonical = Array.isArray(fresh?.data?.drivers)
          ? fresh.data.drivers.find(item => item.id === driverId)
          : null;
      }
      if (!canonicalCategories(canonical?.categories)) throw new Error('category_readback_invalid');
      setDrivers(previous => previous.map(item => item.id === driverId ? { ...item, ...canonical } : item));
      if (!confirmed(canonical)) throw new Error('category_write_unconfirmed');
      toast.success(`${CATEGORY_LABELS[category] || category} ${desired ? 'eingeschaltet' : 'ausgeschaltet'}`);
    } catch (err) {
      const uncertain = !err.response || Number(err.response.status) >= 500
        || err.message === 'category_readback_invalid' || err.message === 'category_write_unconfirmed';
      if (uncertain) {
        try {
          const fresh = await api.get('/partners/drivers');
          const canonical = Array.isArray(fresh?.data?.drivers)
            ? fresh.data.drivers.find(item => item.id === driverId)
            : null;
          if (canonicalCategories(canonical?.categories)) {
            setDrivers(previous => previous.map(item => item.id === driverId ? { ...item, ...canonical } : item));
            if (canonical.categories.active.includes(category) === desired
                && canonical.categories.partner_controlled[category] === desired) {
              toast.success(`${CATEGORY_LABELS[category] || category} ${desired ? 'eingeschaltet' : 'ausgeschaltet'}`);
              return;
            }
          }
        } catch (_) { /* ursprünglichen Fehler unten ehrlich melden */ }
      }
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Kategorie konnte nicht geändert werden');
    } finally {
      categoryLocksRef.current.delete(driverId);
      setCategorySaving(current => { const next = { ...current }; delete next[driverId]; return next; });
    }
  };

  const removeDriver = async (driverId, name) => {
    try {
      await api.delete(`/partners/drivers/${driverId}`);
      toast.success(`${name} aus der Flotte entfernt`);
      setConfirmRemove(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Fehler'); }
  };

  const notApproved = partner?.approval_status !== 'approved';
  const onlineDrivers = drivers.filter(d => d.is_online);
  const fq = fleetSearch.trim().toLowerCase();
  const fleetFiltered = fq ? drivers.filter(d => [d.name, d.email, d.phone].some(x => (x || '').toLowerCase().includes(fq))) : drivers;

  const Toggle = ({ value, onChange, disabled, label }) => (
    <button onClick={onChange} disabled={disabled} role="switch" aria-checked={value} aria-label={label}
      style={{ width: 42, height: 24, borderRadius: 12, background: value ? '#0a0a0a' : '#d1d5db', position: 'relative', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: value ? 21 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  );

  return (
    <div>
      <PageHeader
        title="Meine Fahrer"
        description={<>{drivers.length} Fahrer in der Flotte · {onlineDrivers.length} online</>}
        actions={
        <div style={{ display: 'flex', gap: 8 }}>
          {['fleet', 'invite'].map(v => (
            <button key={v} onClick={() => setView(v)}
              style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: '1.5px solid', borderColor: view === v ? '#000' : '#e5e7eb', background: view === v ? '#000' : '#fff', color: view === v ? '#fff' : '#4b5563' }}>
              {v === 'fleet' ? `Flotte (${drivers.length})` : '+ Fahrer hinzufügen'}
            </button>
          ))}
        </div>
        }
      />

      {notApproved && (
        <div style={{ padding: 12, background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 10, marginBottom: 16, fontSize: 13, color: '#4b5563' }}>
          <Hourglass size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> Dein Konto wird noch geprüft. Fahrer einladen und verwalten ist erst nach Freigabe möglich.
        </div>
      )}

      {/* ── Flotte View ── */}
      {view === 'fleet' && (
        <>
          {drivers.length > 0 && (
            <input value={fleetSearch} onChange={e => setFleetSearch(e.target.value)} placeholder="Fahrer suchen (Name, E-Mail, Telefon)…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', marginBottom: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none' }} />
          )}
          {/* Fahrer Liste mit Controls (Karte nur auf der Live-Karte-Seite) */}
          {drivers.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              <Empty icon={UserPlus} title="Noch keine Fahrer" hint={'Klicke auf "+ Fahrer hinzufügen", um deine Flotte aufzubauen.'} />
            </div>
          ) : fleetFiltered.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              <Empty icon={Search} title="Kein Fahrer passt zur Suche" hint="Suchbegriff oder Filter anpassen." />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {fleetFiltered.map(d => {
                const showStatus = d.approval_status !== 'approved';
                const s = statusBadge[d.approval_status] || statusBadge.pending;
                const rejectionReason = visibleEntityRejectionReason(d.approval_status, d.rejection_reason);
                const categoriesKnown = d.categories
                  && Array.isArray(d.categories.allowed)
                  && Array.isArray(d.categories.active);
                const allowedCategories = categoriesKnown ? d.categories.allowed : [];
                return (
                  <div key={d.id} data-testid={`driver-card-${d.id}`} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>

                    {/* Foto + Online-Punkt */}
                    <DriverAvatar driver={d} />

                    {/* Info */}
                    <div onClick={() => navigate(`/drivers/${d.id}`)} title="Profil öffnen"
                      style={{ flex: 1, minWidth: 160, cursor: 'pointer' }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#111', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>{d.name} <span style={{ fontSize: 12, color: '#4b5563', fontWeight: 600 }}>Profil ›</span></div>
                      <div style={{ fontSize: 12, color: '#6b7280', overflowWrap: 'anywhere' }}>{d.email} · {d.phone || '–'}</div>
                      {(showStatus || d.suspension) && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
                          {showStatus && (
                            <span data-testid={`driver-approval-${d.id}`} style={{ fontSize: 12, fontWeight: 700, color: s.color, background: s.bg, padding: '2px 8px', borderRadius: 20 }}>{s.label}</span>
                          )}
                          {/* FAHRER-KI-BACKEND-001 (Teil D): „gesperrt" mit Kurzgrund — Volltext nur beim Admin */}
                          {d.suspension && (
                            <span data-testid={`driver-suspension-${d.id}`} style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', background: '#fef2f2', padding: '2px 8px', borderRadius: 20 }}>
                              Gesperrt · {d.suspension.short}
                            </span>
                          )}
                        </div>
                      )}
                      {rejectionReason && (
                        <div data-testid={`driver-rejection-reason-${d.id}`}
                          style={{ fontSize: 12, color: '#dc2626', fontWeight: 600, marginTop: 6 }}>
                          Grund: {rejectionReason}
                        </div>
                      )}
                    </div>

                    {/* Tatsächlich zulässige Kategorien. Fehlender/alter Serververtrag bleibt unbekannt
                        und wird nie als eine Reihe ausgeschalteter Kategorien dargestellt. */}
                    <div data-testid={`driver-categories-${d.id}`}
                      style={{ flex: '1 1 330px', minWidth: 230, borderLeft: '1px solid #f3f4f6', paddingLeft: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', marginBottom: 7 }}>Fahrzeugkategorien</div>
                      {!categoriesKnown ? (
                        <div role="status" style={{ fontSize: 12, color: '#6b7280' }}>Status nicht verfügbar</div>
                      ) : allowedCategories.length === 0 ? (
                        <div role="status" style={{ fontSize: 12, color: '#6b7280' }}>Keine zulässige Kategorie verfügbar</div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 6 }}>
                          {allowedCategories.map(category => {
                            const active = d.categories.active.includes(category);
                            const label = CATEGORY_LABELS[category] || category;
                            return (
                              <div key={category} data-testid={`driver-category-${d.id}-${category}`}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa' }}>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{label}</div>
                                  <div style={{ fontSize: 11, fontWeight: 800, color: active ? '#166534' : '#6b7280' }}>{active ? 'Ein' : 'Aus'}</div>
                                </div>
                                <Toggle label={`${label} Fahrzeugkategorie`} value={active}
                                  disabled={!!categorySaving[d.id]}
                                  onChange={() => toggleCategory(d, category, active)} />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Stats */}
                    <div style={{ display: 'flex', gap: 16, flexShrink: 0 }}>
                      <div style={{ textAlign: 'center' }}>
                        <div data-testid={`driver-rides-${d.id}`} style={{ fontSize: 16, fontWeight: 900, color: '#0a0a0a' }}>{d.rides_accepted || 0}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>Fahrten</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div data-testid={`driver-rating-${d.id}`} style={{ fontSize: 16, fontWeight: 900, color: '#4b5563' }}><Star size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> {parseFloat(d.rating || 5).toFixed(1)}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>Bewertung</div>
                      </div>
                    </div>

                    {/* Controls */}
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexShrink: 0, borderLeft: '1px solid #f3f4f6', paddingLeft: 16 }}>

                      {/* Cash toggle */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Banknote size={13} color={d.cash_enabled ? '#0a0a0a' : '#6b7280'} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: d.cash_enabled ? '#0a0a0a' : '#6b7280' }}>Bar</span>
                        </div>
                        <Toggle label="Bargeld" value={!!d.cash_enabled} onChange={() => toggleSetting(d.id, 'cash_enabled', !!d.cash_enabled)} />
                      </div>

                      {/* Auto-accept toggle */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Zap size={13} color={d.auto_accept ? '#4b5563' : '#6b7280'} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: d.auto_accept ? '#4b5563' : '#6b7280' }}>Auto</span>
                        </div>
                        <Toggle label="Auto-Annahme" value={!!d.auto_accept} onChange={() => toggleSetting(d.id, 'auto_accept', !!d.auto_accept)} />
                      </div>

                      {/* Remove */}
                      {confirmRemove === d.id ? (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 700 }}>Sicher?</span>
                          <button onClick={() => removeDriver(d.id, d.name)}
                            style={{ padding: '5px 10px', background: '#dc2626', color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>Ja</button>
                          <button onClick={() => setConfirmRemove(null)}
                            style={{ padding: '5px 8px', background: '#f3f4f6', color: '#4b5563', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>Nein</button>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmRemove(d.id)} title="Fahrer entfernen"
                          style={{ padding: '7px', background: '#fef2f2', color: '#dc2626', borderRadius: 8, border: '1px solid #fecaca', cursor: 'pointer' }}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Einladen View ── */}
      {view === 'invite' && (
        <div style={{ display: 'grid', gap: 16 }}>
          {/* Zwei Karten nebeneinander, schmal gestapelt. Die Tabelle steht BEWUSST außerhalb dieses Rasters:
              eine über alle Spalten gespannte Zeile hält auto-fit-Spalten offen und drückt die Karten auf 1/3–1/4. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 16 }}>
            {/* Registrierten Fahrer per E-Mail oder Telefonnummer einladen — ohne Trefferliste */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>Registrierten Fahrer einladen</h3>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>Gib die E-Mail-Adresse oder Telefonnummer ein, mit der sich der Fahrer in der VOOGO-Fahrer-App registriert hat. Die Einladung erscheint dann in seiner App.</p>
              <form onSubmit={sendContactInvite} style={{ display: 'flex', gap: 8 }}>
                <input value={inviteContact} onChange={e => { setInviteContact(e.target.value); setContactFeedback(null); }}
                  placeholder="E-Mail oder Telefonnummer" autoComplete="off" disabled={notApproved}
                  aria-label="E-Mail-Adresse oder Telefonnummer des Fahrers" data-testid="invite-contact-input"
                  style={{ flex: 1, minWidth: 0, padding: '10px 13px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, opacity: notApproved ? 0.5 : 1 }}
                  onFocus={e => e.target.style.borderColor='#000'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />
                <button type="submit" disabled={contactLoading || notApproved} data-testid="invite-contact-submit"
                  style={{ padding: '10px 16px', background: '#000', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, opacity: notApproved ? 0.4 : 1 }}>
                  <UserPlus size={14} /> {contactLoading ? 'Prüfe…' : 'Einladen'}
                </button>
              </form>
              {contactFeedback && (
                <div role={contactFeedback.kind === 'error' ? 'alert' : 'status'} data-testid="invite-contact-feedback"
                  style={{ marginTop: 12, padding: 10, borderRadius: 8, fontSize: 12, lineHeight: 1.6, fontWeight: 600,
                    color: contactFeedback.kind === 'error' ? '#dc2626' : '#0a0a0a',
                    background: contactFeedback.kind === 'error' ? '#fef2f2' : '#f4f5f6' }}>
                  {contactFeedback.text}
                  {contactFeedback.hint && (
                    <div style={{ marginTop: 4, fontWeight: 500, color: '#4b5563' }}>{contactFeedback.hint}</div>
                  )}
                </div>
              )}
            </div>

            {/* E-Mail Einladung */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>Neuen Fahrer einladen</h3>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>Wir schicken ihm eine E-Mail mit der Anleitung: App laden, mit dieser Adresse registrieren, Nachweise hochladen. Nach der Freigabe lädst du ihn unter „Registrierten Fahrer einladen“ mit dieser Adresse ein.</p>
              <form onSubmit={sendEmailInvite} style={{ display: 'flex', gap: 8 }}>
                <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} type="email" placeholder="fahrer@example.de" required disabled={notApproved}
                  style={{ flex: 1, minWidth: 0, padding: '10px 13px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, opacity: notApproved ? 0.5 : 1 }}
                  onFocus={e => e.target.style.borderColor='#000'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />
                <button type="submit" disabled={inviteLoading || notApproved}
                  style={{ padding: '10px 16px', background: '#000', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, opacity: notApproved ? 0.4 : 1 }}>
                  <Send size={14} /> Einladen
                </button>
              </form>
              {lastInvite && (
                <div style={{ marginTop: 12, padding: 10, background: '#f4f5f6', borderRadius: 8, fontSize: 12, color: '#4b5563', lineHeight: 1.6 }}>
                  Einladung an <b>{lastInvite}</b> ist raus. Sobald er sich mit dieser Adresse
                  registriert hat und freigegeben ist, kannst du ihn unter „Registrierten Fahrer einladen“ mit dieser Adresse einladen.
                </div>
              )}

              {/* Einladungshinweise */}
              <div style={{ marginTop: 16, padding: 12, background: '#f9fafb', borderRadius: 10, fontSize: 12, color: '#4b5563', lineHeight: 1.7 }}>
                <div style={{ fontWeight: 700, color: '#374151', marginBottom: 4 }}>Einstellungen pro Fahrer</div>
                <div><strong><Banknote size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> Bargeld:</strong> Fahrer kann/darf Barzahlungen annehmen</div>
                <div><strong><Zap size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> Auto-Annahme:</strong> Fahrtanfragen werden automatisch angenommen — kein Warten in der App</div>
                <div><strong><Trash2 size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> Entfernen:</strong> Fahrer wird sofort aus der Flotte entfernt und kann keine weiteren Fahrten übernehmen</div>
              </div>
            </div>
          </div>

          {/* Ausstehende Einladungen */}
          {invitations.filter(i => i.status === 'pending').length > 0 && (
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6' }}>
                <h3 style={{ fontSize: 13, fontWeight: 800 }}>Ausstehende Einladungen</h3>
              </div>
              <div style={{ overflowX: 'auto' }}>{/* LESBARKEIT-LAYOUT-001: Tabelle scrollt im Container, nie die Seite */}<table className="vg-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#f9fafb' }}>
                  {['E-Mail / Telefon','Typ','Erstellt','Läuft ab'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', fontSize: 12, color: '#6b7280', fontWeight: 700, textAlign: 'left', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {invitations.filter(i => i.status === 'pending').map(inv => (
                    <tr key={inv.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '11px 14px', fontSize: 13 }}>{inv.email}</td>
                      <td style={{ padding: '11px 14px' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: inv.invite_type === 'driver' ? '#4b5563' : '#6b7280', background: inv.invite_type === 'driver' ? '#f3f4f6' : '#f4f5f6', padding: '3px 9px', borderRadius: 6 }}>
                          {inv.invite_type === 'driver' ? 'In-App' : 'E-Mail Link'}
                        </span>
                      </td>
                      <td style={{ padding: '11px 14px', fontSize: 12, color: '#6b7280' }}>{nurDatum(inv.created_at)}</td>
                      <td style={{ padding: '11px 14px', fontSize: 12, color: '#6b7280' }}>{nurDatum(inv.expires_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
